import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { Loader2, MoreVertical, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from '@/components/ui/sonner';
import { FieldLabel } from '@/components/FormField';
import { SettingsCard } from '@/features/settings/SettingsCard';
import { StatusStrip } from '@/features/settings/StatusStrip';
import { dateLocaleFor } from '@/i18n';
import {
  useAddCertificate,
  useCertificates,
  useRevokeCertificate,
  type CertificateStatus,
  type KsefCertificate,
} from './api';

const STATUS_VARIANT: Record<CertificateStatus, 'default' | 'secondary' | 'outline'> = {
  ACTIVE: 'default',
  REVOKED: 'outline',
  EXPIRED: 'secondary',
};

export function CertificatesSection({ className }: { className?: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? i18n.language;
  const certificates = useCertificates();
  const list = certificates.data ?? [];
  const activeCerts = list.filter((cert) => cert.status === 'ACTIVE');
  const loaded = !certificates.isPending && !certificates.isError;
  const [managing, setManaging] = useState(false);
  const [adding, setAdding] = useState(false);

  return (
    <SettingsCard
      icon={ShieldCheck}
      title={t('certificates.title')}
      description={t('certificates.description')}
      className={className}
      action={
        loaded ? (
          list.length > 0 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  aria-label={t('certificates.options')}
                >
                  <MoreVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setManaging(true)}>
                  {t('certificates.manage')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setAdding(true)}>
                  {t('certificates.add_title')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button type="button" onClick={() => setAdding(true)}>
              <Plus data-icon="inline-start" aria-hidden />
              {t('certificates.add_title')}
            </Button>
          )
        ) : undefined
      }
    >
      {certificates.isPending ? (
        <StatusStrip>
          <span className="text-sm text-muted-foreground">{t('certificates.loading')}</span>
          <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden />
        </StatusStrip>
      ) : certificates.isError ? (
        <StatusStrip>
          <span className="text-sm text-destructive">{t('certificates.load_error')}</span>
        </StatusStrip>
      ) : list.length === 0 ? (
        <StatusStrip>
          <span className="text-sm text-muted-foreground">{t('certificates.empty')}</span>
        </StatusStrip>
      ) : activeCerts.length === 0 ? (
        <StatusStrip>
          <span className="text-sm text-muted-foreground">{t('certificates.no_active')}</span>
        </StatusStrip>
      ) : (
        <div className="space-y-4 [&>*+*]:border-t [&>*+*]:pt-4">
          {activeCerts.map((cert) => (
            <ActiveCertificate key={cert.id} cert={cert} lng={lng} />
          ))}
        </div>
      )}

      <ManageCertificatesDialog open={managing} onOpenChange={setManaging} list={list} lng={lng} />
      <AddCertificateDialog open={adding} onOpenChange={setAdding} />
    </SettingsCard>
  );
}

/**
 * Read-only details of an in-force certificate, shown in the card body as a key-value grid that
 * mirrors the issuer profile summary. Revoking lives in the Manage dialog.
 */
function ActiveCertificate({ cert, lng }: { cert: KsefCertificate; lng: string }) {
  const { t } = useTranslation();
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
      <SummaryRow label={t('certificates.fields.nip')} value={cert.nip} />
      <SummaryRow
        label={t('certificates.valid_until')}
        value={format(parseISO(cert.validUntil), 'd MMM yyyy', { locale: dateLocaleFor(lng) })}
      />
      <SummaryRow label={t('certificates.serial')} value={cert.serialNumber} />
    </dl>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium break-all">{value}</dd>
    </>
  );
}

function CertificateRow({ cert, lng }: { cert: KsefCertificate; lng: string }) {
  const { t } = useTranslation();
  const revoke = useRevokeCertificate();

  const onRevoke = () => {
    if (!window.confirm(t('certificates.revoke_confirm'))) return;
    revoke.mutate(cert.id, {
      onSuccess: () => toast.success(t('certificates.revoked')),
      onError: () => toast.error(t('certificates.revoke_failed')),
    });
  };

  return (
    <li className="flex items-center justify-between gap-3 p-3">
      <div className="min-w-0 leading-tight">
        <div className="flex items-center gap-2">
          <span className="font-medium">{cert.nip}</span>
          <Badge variant={STATUS_VARIANT[cert.status]}>
            {t(`certificates.status.${cert.status}`)}
          </Badge>
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {t('certificates.valid_until')}:{' '}
          {format(parseISO(cert.validUntil), 'd MMM yyyy', {
            locale: dateLocaleFor(lng),
          })}
          {' · '}
          {t('certificates.serial')}: {cert.serialNumber}
        </div>
      </div>
      {cert.status === 'ACTIVE' ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t('certificates.revoke')}
          disabled={revoke.isPending}
          onClick={onRevoke}
        >
          <Trash2 />
        </Button>
      ) : null}
    </li>
  );
}

function ManageCertificatesDialog({
  open,
  onOpenChange,
  list,
  lng,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  list: KsefCertificate[];
  lng: string;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 p-0 sm:max-w-lg">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>{t('certificates.manage')}</DialogTitle>
          <DialogDescription>{t('certificates.description')}</DialogDescription>
        </DialogHeader>
        <ScrollArea className="cn-dialog-scroll">
          <div className="px-6 pb-6">
            <ul className="divide-y rounded-md border" aria-label={t('certificates.title')}>
              {list.map((cert) => (
                <CertificateRow key={cert.id} cert={cert} lng={lng} />
              ))}
            </ul>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function AddCertificateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 p-0 sm:max-w-lg">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>{t('certificates.add_title')}</DialogTitle>
          <DialogDescription>{t('certificates.description')}</DialogDescription>
        </DialogHeader>
        <ScrollArea className="cn-dialog-scroll">
          <div className="px-6 pb-6">
            <AddCertificateForm onAdded={() => onOpenChange(false)} />
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function AddCertificateForm({ onAdded }: { onAdded: () => void }) {
  const { t } = useTranslation();
  const add = useAddCertificate();
  const [nip, setNip] = useState('');
  const [password, setPassword] = useState('');
  const [certFile, setCertFile] = useState<File | null>(null);
  const [keyFile, setKeyFile] = useState<File | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const ready = nip.trim() !== '' && certFile !== null && keyFile !== null;

  const reset = () => {
    setNip('');
    setPassword('');
    setCertFile(null);
    setKeyFile(null);
    formRef.current?.reset();
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    add.mutate(
      {
        nip: nip.trim(),
        certificateFile: certFile!,
        keyFile: keyFile!,
        password: password || undefined,
      },
      {
        onSuccess: () => {
          toast.success(t('certificates.added'));
          reset();
          onAdded();
        },
        onError: () => toast.error(t('certificates.add_failed')),
      },
    );
  };

  return (
    <form ref={formRef} onSubmit={onSubmit} className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <FieldLabel htmlFor="cert-nip" required>
            {t('certificates.fields.nip')}
          </FieldLabel>
          <Input
            id="cert-nip"
            inputMode="numeric"
            value={nip}
            onChange={(e) => setNip(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="space-y-1.5">
          <FieldLabel htmlFor="cert-password">{t('certificates.fields.password')}</FieldLabel>
          <Input
            id="cert-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="off"
          />
          <p className="text-xs text-muted-foreground">
            {t('certificates.password_optional_hint')}
          </p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <FieldLabel htmlFor="cert-crt" required>
            {t('certificates.fields.cert_file')}
          </FieldLabel>
          <Input
            id="cert-crt"
            type="file"
            accept=".crt,.pem,.cer"
            onChange={(e) => setCertFile(e.target.files?.[0] ?? null)}
          />
        </div>
        <div className="space-y-1.5">
          <FieldLabel htmlFor="cert-key" required>
            {t('certificates.fields.key_file')}
          </FieldLabel>
          <Input
            id="cert-key"
            type="file"
            accept=".key,.pem"
            onChange={(e) => setKeyFile(e.target.files?.[0] ?? null)}
          />
        </div>
      </div>

      <Button type="submit" className="w-full" disabled={add.isPending || !ready}>
        {add.isPending ? <Loader2 className="animate-spin" /> : null}
        {t('certificates.submit')}
      </Button>
    </form>
  );
}
